// Persists where a user was (URL + scroll + premium intent) so we can resume
// after they complete sign-in / sign-up — including OAuth redirects.

const KEY = "auth-return-state";
const TTL_MS = 30 * 60 * 1000; // 30 minutes

export interface PremiumUnlockIntent {
  type: "premium_unlock";
  purchaseType: "premium_guide" | "digital_resource";
  entityId: string;
  kind: "one-time" | "subscribe";
}

export type AuthReturnIntent = PremiumUnlockIntent;

export interface AuthReturnState {
  url: string;
  scrollY: number;
  savedAt: number;
  intent?: AuthReturnIntent;
}

const currentPath = () =>
  window.location.pathname + window.location.search + window.location.hash;

const writeState = (state: AuthReturnState) => {
  try {
    const raw = JSON.stringify(state);
    sessionStorage.setItem(KEY, raw);
    localStorage.setItem(KEY, raw);
  } catch {
    /* ignore */
  }
};

export function saveAuthReturn(intent?: AuthReturnIntent) {
  writeState({
    url: currentPath(),
    scrollY: window.scrollY || window.pageYOffset || 0,
    savedAt: Date.now(),
    intent,
  });
}

export function readAuthReturn(): AuthReturnState | null {
  try {
    const raw = sessionStorage.getItem(KEY) ?? localStorage.getItem(KEY);
    if (!raw) return null;
    const state = JSON.parse(raw) as AuthReturnState;
    if (!state || Date.now() - state.savedAt > TTL_MS) {
      clearAuthReturn();
      return null;
    }
    return state;
  } catch {
    return null;
  }
}

export function clearAuthReturn() {
  try {
    sessionStorage.removeItem(KEY);
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}

/** Returns the saved scroll target for the current URL (or null) without clearing intent. */
export function getScrollTargetForCurrentUrl(): number | null {
  const state = readAuthReturn();
  if (!state) return null;
  if (state.url !== currentPath()) return null;
  return state.scrollY ?? 0;
}

/** Mark scroll restoration as complete (preserves intent so gates can still consume it). */
export function markScrollRestored() {
  const state = readAuthReturn();
  if (!state) return;
  if (!state.intent) {
    clearAuthReturn();
    return;
  }
  writeState({ ...state, scrollY: 0 });
}

/** Pulls and clears a matching intent. */
export function consumeAuthIntent(
  match: (intent: AuthReturnIntent) => boolean,
): AuthReturnIntent | null {
  const state = readAuthReturn();
  if (!state?.intent || !match(state.intent)) return null;
  const remaining: AuthReturnState = { ...state, intent: undefined };
  if (remaining.scrollY) writeState(remaining);
  else clearAuthReturn();
  return state.intent;
}
